import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { FormFooter } from "@/components/FormFooter";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { availableModules } from "@/types/modules";
import { Loader2, RefreshCw, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { rolesAPI, type ModulePermission, type Rol } from "@/lib/api";
import { ensureDashboardRelatedModulesFull } from "@/lib/dashboardCards";
import {
  canonicalSectionKey,
  canonicalizeModulePermissionEntries,
  canonicalizeSections,
  getExplicitSectionPermissionLevel,
  getModulePermissionLevel,
  getSectionPermissionLevel,
  type PermissionLevel,
  type SectionKey,
} from "@/lib/modulePermissions";
import { abmCodigoMeansAlta, adoptCodigoFromListingId } from "@/lib/abmCreateIdentity";
import { RolesUsuariosPanel } from "./RolesUsuariosPanel";
import {
  MODULE_NESTED_SECTIONS,
  ROLE_LEVELS,
  ROLE_MATRIX_ONLY_MODULES,
  ROLE_MODULE_GROUPS,
  ROLE_MODULE_HINTS,
} from "./rolePermissionCatalog";
import { NumberField } from "@/components/ui/number-field";

const IMMORTAL_ROL_CODIGOS = new Set(["rol001", "root"]);

function isImmortalRol(
  rol: { nombre?: string; codigo?: string; isImmortal?: boolean } | null | undefined,
): boolean {
  if (!rol) return false;
  const codigo = (rol.codigo || "").trim().toLowerCase();
  // Exact codigo only. Blank codigo is not system. Display name never locks.
  return codigo !== "" && IMMORTAL_ROL_CODIGOS.has(codigo);
}

function apiErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object" || !("status" in error)) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== "object") return fallback;
  const e = error as {
    message?: unknown;
    details?: unknown;
    data?: Record<string, unknown>;
  };
  if (typeof e.message === "string" && e.message.trim() && e.message !== "Bad Request") {
    return e.message;
  }
  const details = e.details ?? e.data;
  if (typeof details === "string" && details.trim()) return details;
  if (Array.isArray(details) && typeof details[0] === "string") return details[0];
  if (details && typeof details === "object") {
    const d = details as Record<string, unknown>;
    if (typeof d.detail === "string" && d.detail.trim()) return d.detail;
    if (Array.isArray(d.detail) && typeof d.detail[0] === "string") return d.detail[0];
    if (typeof d.message === "string" && d.message.trim()) return d.message;
  }
  return fallback;
}

interface RolesABMProps {
  selectedItem?: Rol | null;
  onBack?: () => void;
  onSaved?: () => void;
  onRolCreated?: () => void;
}

type FormState = {
  codigo: string;
  nombre: string;
  descripcion: string;
  sessionDuration: number;
  canonico: boolean;
  bloquearPersonalizadosExistentes: boolean;
  modulosPermitidos: ModulePermission[];
};

const emptyForm = (): FormState => ({
  codigo: "",
  nombre: "",
  descripcion: "",
  sessionDuration: 15,
  canonico: false,
  bloquearPersonalizadosExistentes: false,
  modulosPermitidos: [],
});

function permissionOf(
  list: ModulePermission[],
  moduleId: number,
): PermissionLevel {
  return getModulePermissionLevel(list, moduleId);
}

function sectionOf(
  list: ModulePermission[],
  moduleId: number,
  sectionId: SectionKey,
): PermissionLevel {
  return getSectionPermissionLevel(list, moduleId, sectionId);
}

function explicitSectionOf(
  list: ModulePermission[],
  moduleId: number,
  sectionId: SectionKey,
): PermissionLevel {
  return getExplicitSectionPermissionLevel(list, moduleId, sectionId);
}

function derivedFlags(level: PermissionLevel): Pick<
  ModulePermission,
  "abmAccess" | "listadosAccess" | "actionColumn"
> {
  if (level === "full") {
    return { abmAccess: true, listadosAccess: true, actionColumn: true };
  }
  if (level === "read") {
    return { abmAccess: false, listadosAccess: true, actionColumn: false };
  }
  return { abmAccess: false, listadosAccess: false, actionColumn: false };
}

function upsertModule(
  list: ModulePermission[],
  moduleId: number,
  level: PermissionLevel,
  sections?: ModulePermission["sections"],
): ModulePermission[] {
  const existing = list.find((p) => p.moduleId === moduleId);
  const nextSections = sections ?? existing?.sections;
  if (level === "none") {
    return list.filter((p) => p.moduleId !== moduleId);
  }
  const entry: ModulePermission = {
    moduleId,
    permission: level,
    ...derivedFlags(level),
    ...(nextSections && Object.keys(nextSections).length ? { sections: nextSections } : {}),
  };
  if (existing) {
    return list.map((p) => (p.moduleId === moduleId ? entry : p));
  }
  return [...list, entry];
}

function setSectionOnList(
  list: ModulePermission[],
  moduleId: number,
  sectionId: SectionKey,
  level: PermissionLevel,
): ModulePermission[] {
  const parent = permissionOf(list, moduleId);
  if (parent === "none") return list;
  const existing = list.find((p) => p.moduleId === moduleId);
  const sections = canonicalizeSections({
    ...(existing?.sections || {}),
    [canonicalSectionKey(sectionId)]: level,
  });
  return upsertModule(list, moduleId, parent, sections);
}

/** Toggle: off persiste `none` (clave canónica); on hereda (se borra la clave). */
function setSectionVisibleOnList(
  list: ModulePermission[],
  moduleId: number,
  sectionId: SectionKey,
  visible: boolean,
): ModulePermission[] {
  const parent = permissionOf(list, moduleId);
  if (parent === "none") return list;
  const existing = list.find((p) => p.moduleId === moduleId);
  const sections = canonicalizeSections(existing?.sections);
  const canon = canonicalSectionKey(sectionId);
  if (!visible) {
    sections[canon] = "none";
    return upsertModule(list, moduleId, parent, sections);
  }
  delete sections[canon];
  return upsertModule(list, moduleId, parent, sections);
}

function ModuleGlyph({
  icon: Icon,
  gradient,
}: {
  icon: LucideIcon;
  gradient: string;
}) {
  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${gradient} shadow-md`}
    >
      <Icon className="h-4 w-4 text-white" />
    </div>
  );
}

function levelLabel(level: PermissionLevel): string {
  return ROLE_LEVELS.find((lv) => lv.id === level)?.label ?? "Ninguno";
}

function LevelRadios({
  value,
  onChange,
  disabled,
  name,
}: {
  value: PermissionLevel;
  onChange: (next: PermissionLevel) => void;
  disabled?: boolean;
  name: string;
}) {
  return (
    <RadioGroup
      value={value}
      onValueChange={(v) => onChange(v as PermissionLevel)}
      disabled={disabled}
      className="flex flex-row flex-wrap gap-3"
    >
      {ROLE_LEVELS.map((lv) => {
        const id = `${name}-${lv.id}`;
        return (
          <div key={lv.id} className="flex items-center gap-1.5">
            <RadioGroupItem value={lv.id} id={id} disabled={disabled} />
            <Label htmlFor={id} className="cursor-pointer text-xs font-normal">
              {lv.label}
            </Label>
          </div>
        );
      })}
    </RadioGroup>
  );
}

export function RolesABM({ selectedItem, onBack, onSaved: _onSaved, onRolCreated }: RolesABMProps) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [usuariosBusy, setUsuariosBusy] = useState(false);
  const usuariosBusyRef = React.useRef(false);
  const [roles, setRoles] = useState<Rol[]>([]);
  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [originalFormData, setOriginalFormData] = useState<FormState>(emptyForm);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const loadedRol = formData.codigo
    ? roles.find((r) => r.codigo === formData.codigo)
    : selectedItem?.id != null
      ? roles.find((r) => r.id === selectedItem.id)
      : undefined;
  const immortal =
    isImmortalRol(loadedRol) ||
    isImmortalRol(selectedItem) ||
    isImmortalRol({ codigo: formData.codigo });
  const persistedCodigo = (formData.codigo || loadedRol?.codigo || selectedItem?.codigo || "").trim();

  const loadRoles = async () => {
    setLoading(true);
    try {
      const data = await rolesAPI.list();
      setRoles(data);
      const adoptCodigo = (prev: FormState): FormState => {
        const match = adoptCodigoFromListingId(prev.codigo, data, selectedItem?.id);
        if (match?.codigo) return { ...prev, codigo: match.codigo };
        return prev;
      };
      setFormData(adoptCodigo);
      setOriginalFormData(adoptCodigo);
    } catch (error) {
      console.error("Error loading roles:", error);
      toast.error("Error al cargar roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRoles();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "F10") return;
      e.preventDefault();
      if (!persistedCodigo) {
        toast.error("Seleccioná un rol para eliminar");
        return;
      }
      if (immortal) {
        return;
      }
      setShowDeleteDialog(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [persistedCodigo, immortal]);

  useEffect(() => {
    if (selectedItem) {
      selectRol(selectedItem);
    } else {
      const blank = emptyForm();
      setFormData(blank);
      setOriginalFormData(blank);
      setHasChanges(false);
    }
  }, [selectedItem]);

  const applyForm = (data: FormState) => {
    setFormData(data);
    setHasChanges(JSON.stringify(data) !== JSON.stringify(originalFormData));
  };

  const selectRol = (rol: Rol) => {
    const loaded: FormState = {
      codigo: rol.codigo,
      nombre: rol.nombre,
      descripcion: rol.descripcion || "",
      sessionDuration: rol.sessionDuration ?? 15,
      canonico: Boolean(rol.canonico),
      bloquearPersonalizadosExistentes: Boolean(rol.bloquearPersonalizadosExistentes),
      modulosPermitidos: canonicalizeModulePermissionEntries(rol.modulosPermitidos || []),
    };
    setFormData(loaded);
    setOriginalFormData(loaded);
    setHasChanges(false);
  };

  const setModuleLevel = (moduleId: number, level: PermissionLevel) => {
    applyForm({
      ...formData,
      modulosPermitidos: upsertModule(formData.modulosPermitidos, moduleId, level),
    });
  };

  const setSectionLevel = (moduleId: number, sectionId: SectionKey, level: PermissionLevel) => {
    applyForm({
      ...formData,
      modulosPermitidos: setSectionOnList(
        formData.modulosPermitidos,
        moduleId,
        sectionId,
        level,
      ),
    });
  };

  const setSectionVisible = (moduleId: number, sectionId: SectionKey, visible: boolean) => {
    let next = setSectionVisibleOnList(
      formData.modulosPermitidos,
      moduleId,
      sectionId,
      visible,
    );
    if (visible) {
      next = ensureDashboardRelatedModulesFull(next);
    }
    applyForm({ ...formData, modulosPermitidos: next });
  };

  /** Opt-in: ON = full explícito; OFF = none (no hereda el padre). */
  const setSectionOptIn = (moduleId: number, sectionId: SectionKey, enabled: boolean) => {
    applyForm({
      ...formData,
      modulosPermitidos: setSectionOnList(
        formData.modulosPermitidos,
        moduleId,
        sectionId,
        enabled ? "full" : "none",
      ),
    });
  };

  const setGroupFull = (moduleIds: number[]) => {
    let next = formData.modulosPermitidos;
    for (const id of moduleIds) next = upsertModule(next, id, "full");
    applyForm({ ...formData, modulosPermitidos: next });
  };

  const handleSave = async () => {
    if (!formData.nombre || !formData.descripcion) {
      toast.error("Complete todos los campos requeridos");
      return;
    }
    setProcessing(true);
    try {
      const payload = {
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        sessionDuration: formData.sessionDuration,
        canonico: formData.canonico,
        bloquearPersonalizadosExistentes: formData.bloquearPersonalizadosExistentes,
        modulosPermitidos: ensureDashboardRelatedModulesFull(
          canonicalizeModulePermissionEntries(formData.modulosPermitidos),
        ),
        activo: true,
      };
      if (abmCodigoMeansAlta(formData.codigo)) {
        await rolesAPI.create(payload);
        toast.success("Rol creado exitosamente");
      } else {
        await rolesAPI.update(formData.codigo, payload);
        toast.success("Rol actualizado exitosamente");
      }
      if (onRolCreated) onRolCreated();
      goToListado();
    } catch (error: unknown) {
      console.error("Save error:", error);
      toast.error(error instanceof Error ? error.message : "Error al guardar rol");
    } finally {
      setProcessing(false);
    }
  };

  const goToListado = () => {
    setShowDeleteDialog(false);
    if (onBack) onBack();
    else if (_onSaved) _onSaved();
  };

  const waitUsuariosIdle = async () => {
    const started = Date.now();
    while (usuariosBusyRef.current && Date.now() - started < 8000) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  };

  const handleConfirmDelete = async () => {
    if (immortal) {
      setShowDeleteDialog(false);
      return;
    }
    const codigo = persistedCodigo;
    if (!codigo) {
      toast.error("Seleccioná un rol para eliminar");
      setShowDeleteDialog(false);
      return;
    }
    setProcessing(true);
    try {
      await waitUsuariosIdle();
      try {
        const still = await rolesAPI.listUsuarios(codigo);
        if (still.length > 0) {
          toast.error(
            "No se puede eliminar el rol mientras tenga usuarios asignados. Mové los usuarios a otro rol.",
          );
          return;
        }
      } catch (error: unknown) {
        if (apiErrorStatus(error) === 404) {
          goToListado();
          return;
        }
        throw error;
      }
      await rolesAPI.delete(codigo);
      toast.success("Rol eliminado");
      goToListado();
    } catch (error: unknown) {
      if (apiErrorStatus(error) === 404) {
        goToListado();
        return;
      }
      toast.error(apiErrorMessage(error, "Error al eliminar rol"));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end mb-2">
        <Button variant="outline" size="sm" onClick={() => void loadRoles()}>
          <RefreshCw className="w-4 h-4 mr-2" /> Recargar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código</label>
          <input
            type="text"
            value={formData.codigo}
            readOnly
            tabIndex={-1}
            className="w-full px-4 py-2 border border-input bg-muted rounded-lg focus:outline-none text-sm"
            placeholder="Auto-generado"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nombre del rol</label>
          <input
            type="text"
            value={formData.nombre}
            onChange={(e) => applyForm({ ...formData, nombre: e.target.value })}
            placeholder="Escriba el nombre del rol..."
            tabIndex={1}
            autoComplete="off"
            className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Duración de sesión (min)</label>
          <NumberField
            min={1}
            max={1440}
            value={formData.sessionDuration ?? 15}
            onChange={(e) =>
              applyForm({ ...formData, sessionDuration: parseInt(e.target.value, 10) || 0 })
            }
            tabIndex={3}
            className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Inactividad antes de cerrar sesión. Por defecto 15.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
        <Switch
          checked={formData.canonico}
          onCheckedChange={(on) => applyForm({ ...formData, canonico: on === true })}
        />
        <span className="text-sm font-medium">Super Usuario</span>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
        <Checkbox
          id="bloquear-personalizados"
          className="mt-0.5"
          checked={formData.bloquearPersonalizadosExistentes}
          onCheckedChange={(on) =>
            applyForm({ ...formData, bloquearPersonalizadosExistentes: on === true })
          }
        />
        <label htmlFor="bloquear-personalizados" className="text-sm">
          <span className="font-medium">No seleccionar ítems personalizados existentes</span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            En ventas y presupuestos, + Pers. solo crea uno nuevo. El buscador no muestra personalizados ya cargados.
          </span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Descripción</label>
        <textarea
          value={formData.descripcion}
          onChange={(e) => applyForm({ ...formData, descripcion: e.target.value })}
          tabIndex={2}
          className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          rows={3}
          placeholder="Descripción del rol y sus permisos..."
        />
      </div>

      <div className="border-t border-border pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.9fr)] gap-6">
          <div>
            <label className="block text-sm font-medium mb-3">Permisos de módulos</label>
            <Accordion
              key={formData.codigo || "nuevo"}
              type="multiple"
              className="rounded-lg border border-border"
            >
              {ROLE_MODULE_GROUPS.map((g) => (
                <div key={g.id} className="border-b border-border last:border-b-0">
                  <div className="flex items-center justify-between gap-3 px-3 pt-3 pb-1">
                    <span className="text-xs font-medium text-muted-foreground">{g.title}</span>
                    <button
                      type="button"
                      className="text-xs font-normal text-muted-foreground hover:text-foreground"
                      onClick={() => setGroupFull(g.moduleIds)}
                    >
                      Todo el grupo
                    </button>
                  </div>
                  {g.moduleIds.map((id) => {
                    const moduleMeta =
                      availableModules.find((m) => m.id === id) ||
                      ROLE_MATRIX_ONLY_MODULES.find((m) => m.id === id);
                    const level = permissionOf(formData.modulosPermitidos, id);
                    const kids = MODULE_NESTED_SECTIONS[id];
                    return (
                      <AccordionItem
                        key={id}
                        value={`mod-${id}`}
                        className={`box-border border-border border-l-4 px-3 ${
                          level !== "none"
                            ? "border-l-emerald-600/75 dark:border-l-emerald-400/65"
                            : "border-l-muted-foreground/40 dark:border-l-muted-foreground/35"
                        }`}
                      >
                        <AccordionTrigger className="py-2.5 hover:no-underline">
                          <span className="flex min-w-0 flex-1 items-center gap-2.5 pr-2 text-left">
                            {moduleMeta ? (
                              <ModuleGlyph icon={moduleMeta.icon} gradient={moduleMeta.gradient} />
                            ) : null}
                            <span className="min-w-0 flex-1 truncate text-sm font-medium">
                              {moduleMeta?.name || `Módulo ${id}`}
                            </span>
                            <span className="shrink-0 text-xs font-normal text-muted-foreground">
                              {levelLabel(level)}
                            </span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-3">
                          {ROLE_MODULE_HINTS[id] ? (
                            <p className="mb-2 text-xs text-muted-foreground">{ROLE_MODULE_HINTS[id]}</p>
                          ) : null}
                          <LevelRadios
                            name={`mod-${id}`}
                            value={level}
                            onChange={(lv) => setModuleLevel(id, lv)}
                          />
                          {kids?.map((kid) => {
                            const KidIcon = kid.icon;
                            const parentOff = level === "none";
                            const secLevel = sectionOf(formData.modulosPermitidos, id, kid.id);
                            const optInOn =
                              !parentOff &&
                              explicitSectionOf(formData.modulosPermitidos, id, kid.id) === "full";
                            return (
                              <div
                                key={kid.id}
                                className={`mt-3 border-l border-border pl-3 ${parentOff ? "opacity-45" : ""}`}
                              >
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                  <div className="flex min-w-0 flex-1 items-start gap-2.5">
                                    <ModuleGlyph icon={KidIcon} gradient={kid.gradient} />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium">{kid.name}</p>
                                      <p className="text-xs text-muted-foreground">{kid.hint}</p>
                                    </div>
                                  </div>
                                  {kid.kind === "level" ? (
                                    <LevelRadios
                                      name={`sec-${id}-${kid.id}`}
                                      value={secLevel}
                                      disabled={parentOff}
                                      onChange={(lv) => setSectionLevel(id, kid.id, lv)}
                                    />
                                  ) : kid.kind === "opt_in" ? (
                                    <label className="flex shrink-0 items-center gap-2 text-xs">
                                      <Checkbox
                                        id={`sec-opt-${id}-${kid.id}`}
                                        checked={optInOn}
                                        disabled={parentOff}
                                        onCheckedChange={(on) =>
                                          setSectionOptIn(id, kid.id, on === true)
                                        }
                                      />
                                      Permitir
                                    </label>
                                  ) : (
                                    <label className="flex shrink-0 items-center gap-2 text-xs">
                                      <Checkbox
                                        id={`sec-vis-${id}-${kid.id}`}
                                        checked={!parentOff && secLevel !== "none"}
                                        disabled={parentOff}
                                        onCheckedChange={(on) =>
                                          setSectionVisible(id, kid.id, on === true)
                                        }
                                      />
                                      Visible
                                    </label>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </div>
              ))}
            </Accordion>
          </div>

          <div className="rounded-lg border border-border p-4 bg-muted/20">
            {persistedCodigo ? (
              <RolesUsuariosPanel
                rolCodigo={persistedCodigo}
                rolNombre={formData.nombre || "este rol"}
                onRoleGone={goToListado}
                onBusyChange={(busy) => {
                  usuariosBusyRef.current = busy;
                  setUsuariosBusy(busy);
                }}
              />
            ) : (
              <div>
                <h3 className="text-sm font-medium">Usuarios con este rol</h3>
                <p className="text-xs text-muted-foreground mt-2">
                  Guardá el rol para poder mover personas acá. El combo de Usuarios al
                  crear/editar una persona se queda.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <FormFooter
        onBack={onBack}
        onDelete={() => setShowDeleteDialog(true)}
        deleteDisabled={!persistedCodigo || immortal || processing || usuariosBusy}
        onSave={() => void handleSave()}
        saveDisabled={!hasChanges || processing}
        saving={processing}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar rol</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar el rol {formData.nombre}? Si todavía hay usuarios asignados, primero
              movelos a otro rol. F10 abre este mismo diálogo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => void handleConfirmDelete()}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
